import { useMergeLink } from "@mergeapi/react-merge-link";
import { LoadingButton } from "@mui/lab";
import { List, ListItem, ListItemText } from "@mui/material";
import { FC, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useApiService } from "../../../server/api/client";
import { useSupabase, useSupabaseCallback } from "../../../server/supabase/hooks";
import { Users } from "../../../styles/icons/users";
import { IntegrationCard } from "../../generic/components/cards/integration-card";
import {
  HRISIntegrationService,
  SyncStatusResponse,
} from "../../integrations/client/hris-integration-service";
import {
  HrisConfig,
  SupabaseHRISService,
  SupabaseSuperAdminHRISService,
} from "../../integrations/server/supabase-hris-service";
import { PersonConflictDataGrid } from "./person-conflict-data-grid";

export const HRISIntegrationCard: FC<{ onConnect?: () => Promise<void> }> = ({ onConnect }) => {
  const { t } = useTranslation();

  const [integration, setIntegration] = useState<HrisConfig>();
  const [syncStatus, setSyncStatus] = useState<SyncStatusResponse | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  useSupabase(async ({ supabase }) => {
    const { data } = await new SupabaseHRISService(supabase).maybeGet();
    setIntegration(data ?? undefined);
  }, []);

  const refreshSyncStatus = useSupabaseCallback<[], void, "super_admin">(
    async ({ supabase }) => {
      if (!integration) return;

      setRefreshing(true);

      const { error, data } = await new SupabaseSuperAdminHRISService(supabase).getSyncStatus();
      if (error || !data) {
        toast.error(t("There was an error getting sync status."));
        return setRefreshing(false);
      }

      const responseData = JSON.parse(data.content) as { results: SyncStatusResponse[] };

      const result = responseData.results;
      if (!result || result.length === 0) {
        toast.error(t("There was an error getting sync status."));
        return setRefreshing(false);
      }

      const employeeSyncStatus = responseData.results.find((res) => res.model_name === "Employee");
      const status = employeeSyncStatus?.status;
      if (!status) {
        toast.error(t("There was an error getting sync status."));
        return setRefreshing(false);
      }

      setSyncStatus({
        model_name: employeeSyncStatus.model_name,
        last_sync_start: employeeSyncStatus.last_sync_start,
        next_sync_start: employeeSyncStatus.next_sync_start,
        is_initial_sync: employeeSyncStatus.is_initial_sync,
        status,
      });

      toast.success(t("Status Refreshed"));
      setRefreshing(false);
    },
    [integration, t],
    "super_admin",
  );

  useEffect(() => {
    void refreshSyncStatus().catch(console.error);
  }, [refreshSyncStatus]);

  const disconnectHRIS = useSupabaseCallback<[], void, "super_admin">(
    async ({ supabase }) => {
      await new SupabaseSuperAdminHRISService(supabase).disconnect();
    },
    [],
    "super_admin",
  );

  const disableIntegration = useSupabaseCallback(
    async ({ supabase }) => {
      await disconnectHRIS();

      const error = await new SupabaseHRISService(supabase).delete();

      if (!error) {
        setIntegration(undefined);
        toast.success(t("HRIS successfully disconnected"));
      } else {
        toast.error(t("There was an error disconnecting your HRIS, please contact support."));
      }
    },
    [disconnectHRIS, t],
  );

  return (
    <IntegrationCard
      title={"HRIS"}
      subtitle={t("Sync your employees using your HRIS system.")}
      hasIntegration={integration !== undefined}
      disableIntegration={disableIntegration}
      connectButton={
        integration ? null : <ConnectButton setIntegration={setIntegration} onConnect={onConnect} />
      }
      icon={<Users sx={{ color: "text.primary" }} />}
      extra={
        syncStatus && integration ? (
          <>
            <ExtraInfo
              syncStatus={syncStatus}
              fullSyncTime={integration.last_sync_time}
              refreshSyncStatus={refreshSyncStatus}
              loading={refreshing}
            />
            <PersonConflictDataGrid />
          </>
        ) : undefined
      }
    />
  );
};

const ExtraInfo: FC<{
  syncStatus: SyncStatusResponse;
  fullSyncTime: string | null;
  refreshSyncStatus: () => Promise<void>;
  loading: boolean;
}> = ({ syncStatus, fullSyncTime, refreshSyncStatus, loading }) => {
  const fmtDate = (dateStr: string): string => {
    const parsed = new Date(dateStr);
    return parsed.toLocaleString();
  };

  return (
    <>
      <List dense={true}>
        <ListItem>
          <ListItemText
            primary="HRIS read at"
            secondary={
              syncStatus.last_sync_start ? fmtDate(syncStatus.last_sync_start) : "No sync completed"
            }
            primaryTypographyProps={{ variant: "subtitle2" }}
          />
        </ListItem>
        <ListItem>
          <ListItemText
            primary="Next sync will start at"
            secondary={
              syncStatus.next_sync_start ? fmtDate(syncStatus.next_sync_start) : "No sync scheduled"
            }
            primaryTypographyProps={{ variant: "subtitle2" }}
          />
        </ListItem>
        <ListItem>
          <ListItemText
            primary="Current HRIS read is"
            secondary={syncStatus.status}
            primaryTypographyProps={{ variant: "subtitle2" }}
          />
        </ListItem>
        <ListItem>
          <ListItemText
            primary="Last successful sync"
            secondary={fullSyncTime ? fmtDate(fullSyncTime) : "No sync completed"}
            primaryTypographyProps={{ variant: "subtitle2" }}
          />
        </ListItem>
      </List>
      <LoadingButton
        variant="outlined"
        loading={loading}
        onClick={() => {
          void refreshSyncStatus().catch(console.error);
        }}
      >
        Refresh Status
      </LoadingButton>
    </>
  );
};

const ConnectButton: FC<{
  setIntegration: (integration: HrisConfig) => void;
  onConnect?: () => Promise<void>;
}> = ({ setIntegration, onConnect }) => {
  const { t } = useTranslation();

  const [linkToken, setLinkToken] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);

  const onSuccess = useSupabaseCallback<[string], void, "super_admin">(
    async ({ supabase }, link_token: string) => {
      const { error, data } = await new SupabaseSuperAdminHRISService(supabase).connect(link_token);

      if (error || !data) toast.error(t("Something went wrong"));
      else {
        await onConnect?.();
        setIntegration(data as HrisConfig);
      }

      setLoading(false);
    },
    [onConnect, setIntegration, t],
    "super_admin",
  );

  useApiService(
    HRISIntegrationService,
    async ({ apiService }) => {
      const { data, error } = await apiService.getLocalToken();
      if (!data || error) toast.error(t("Something went wrong"));
      else setLinkToken(data.link_token);

      setLoading(false);
    },
    [],
  );

  const config = {
    linkToken,
    onSuccess: (v: string) => void onSuccess(v).catch(console.error),
    onExit: () => setLoading(false),
  };

  const { open, isReady } = useMergeLink(config);

  return (
    <LoadingButton
      loading={loading}
      disabled={!isReady || linkToken === ""}
      onClick={() => {
        setLoading(true);
        open();
      }}
      variant="outlined"
      color="success"
    >
      {t("Connect")}
    </LoadingButton>
  );
};
